import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";
import SEO from "../components/SEO";

const AccessibilityStatement = () => {
  useEffect(() => {
    document.title = "Accessibility Statement | Piatto";
    window.scrollTo(0, 0);
    const globalHeader = document.getElementById('header-div');
    if (globalHeader) globalHeader.style.display = 'none';
    return () => { if (globalHeader) globalHeader.style.display = ''; };
  }, []);

  return (
    <>
      <SEO
        title="Accessibility Statement | Piatto"
        description="Piatto's commitment to digital and physical accessibility in hospitality for people with disabilities, carers, and support workers."
      />
      <div className="a11y-page">
        <a href="#a11y-main" className="a11y-skip">Skip to main content</a>

        <header className="a11y-header" role="banner">
          <div className="a11y-header-inner">
            <Link to="/" className="a11y-logo-link" aria-label="Piatto - Return to home page">
              <img src={logo} alt="Piatto" className="a11y-logo" />
            </Link>
            <nav aria-label="Page navigation">
              <Link to="/" className="a11y-nav-link">Home</Link>
            </nav>
          </div>
        </header>

        <main id="a11y-main" className="a11y-main" role="main" aria-label="Accessibility Statement">
          <article className="a11y-content" aria-labelledby="a11y-title">
            <h1 id="a11y-title">Accessibility Statement</h1>
            <p className="a11y-updated"><time dateTime="2026-08">Last updated: August 2026</time></p>

            <section aria-labelledby="a11y-commitment">
              <h2 id="a11y-commitment">Our Commitment</h2>
              <p>
                Piatto exists because hospitality should be for everyone. We are an Australian platform
                built to bridge the gap between venues and the people they serve — including people with
                disabilities, carers, support workers, and anyone who needs to know what to expect before
                they arrive.
              </p>
              <p>
                Accessibility is not an afterthought at Piatto. It is why we exist. We are committed to
                ensuring that our website, mobile app, and the hospitality experiences we help facilitate
                are accessible to the widest possible audience, regardless of ability or circumstance.
              </p>
            </section>

            <section aria-labelledby="a11y-standards">
              <h2 id="a11y-standards">Standards We Target</h2>
              <p>
                We strive to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong> across
                our web platform and mobile application. These guidelines are developed by the World Wide
                Web Consortium (W3C) and represent the internationally recognised standard for web accessibility.
              </p>
              <p>
                In 2026, we completed a comprehensive WCAG 2.1 AA remediation across our web application,
                addressing colour contrast, landmark structure, form accessibility, loading states, data
                tables, modal dialogs, and screen reader support.
              </p>
            </section>

            <section aria-labelledby="a11y-website">
              <h2 id="a11y-website">This Website</h2>
              <p>Our landing page and web application include the following accessibility features:</p>

              <h3 id="a11y-typography">Typography and Readability</h3>
              <ul aria-labelledby="a11y-typography">
                <li>
                  <strong>Atkinson Hyperlegible Next</strong> — our primary typeface, designed by the
                  Braille Institute specifically for legibility, particularly for people with low vision.
                  Paired with Lexend, a typeface designed to improve reading proficiency.
                </li>
                <li>
                  <strong>Adjustable text sizing</strong> — a toolbar allows visitors to increase text
                  size through four levels, with the choice saved for return visits.
                </li>
                <li>
                  <strong>High contrast</strong> — text meets WCAG 2.1 AA contrast ratios (4.5:1 minimum).
                  Focus indicators use a visible 3px outline.
                </li>
              </ul>

              <h3 id="a11y-assistive">Assistive Features</h3>
              <ul aria-labelledby="a11y-assistive">
                <li>
                  <strong>Read Aloud</strong> — a built-in feature that reads page content aloud using
                  the Web Speech API, highlighting each element as it is spoken.
                </li>
                <li>
                  <strong>Skip to main content</strong> — a skip link allows keyboard and screen reader
                  users to bypass repetitive navigation.
                </li>
                <li>
                  <strong>Reduced motion</strong> — all animations are disabled for users who indicate
                  a preference for reduced motion, via both CSS media queries and JavaScript detection.
                </li>
              </ul>

              <h3 id="a11y-navigation">Navigation and Interaction</h3>
              <ul aria-labelledby="a11y-navigation">
                <li>
                  <strong>Keyboard navigation</strong> — all interactive elements are reachable and
                  operable by keyboard. The mobile menu includes a focus trap and Escape key support.
                </li>
                <li>
                  <strong>Screen reader support</strong> — semantic HTML, ARIA landmarks, live regions
                  for dynamic content, and descriptive labels throughout. Decorative elements are hidden
                  from assistive technology.
                </li>
                <li>
                  <strong>Accessible forms</strong> — all form fields have associated labels, required
                  fields are programmatically indicated, error messages are announced to screen readers,
                  and inputs include appropriate autocomplete attributes.
                </li>
                <li>
                  <strong>Descriptive alt text</strong> — all meaningful images include descriptive
                  alternative text. Decorative images are marked appropriately.
                </li>
              </ul>
            </section>

            <section aria-labelledby="a11y-app">
              <h2 id="a11y-app">The Piatto Mobile App</h2>
              <p>
                The Piatto app is designed so that people with disabilities can participate in hospitality
                independently.
              </p>

              <h3 id="a11y-ordering">Ordering Without Barriers</h3>
              <ul aria-labelledby="a11y-ordering">
                <li>
                  <strong>Screen-reader-accessible menus</strong> — most online venue menus cannot be
                  read by a screen reader. On Piatto, they can — so blind and low-vision customers can
                  browse and order independently.
                </li>
                <li>
                  <strong>Submit Upon Approach</strong> — for guests who find it difficult to get to the
                  counter, this feature lets them order on approach. The venue knows they are coming and
                  can bring the order to them.
                </li>
                <li>
                  <strong>Submit Once Seated</strong> — guests can scan a table QR code and order from
                  their seat without needing to queue or flag down staff.
                </li>
                <li>
                  <strong>No voice required</strong> — users who are deaf, hard of hearing, or
                  non-verbal can order without speaking or listening for a called name. Updates are
                  sent directly to their phone.
                </li>
              </ul>

              <h3 id="a11y-personalisation">Personalisation and Planning</h3>
              <ul aria-labelledby="a11y-personalisation">
                <li>
                  <strong>Set your needs once</strong> — users save their accessibility requirements
                  (mobility, sensory, dietary, cognitive) to their profile. These needs are shared with
                  the venue automatically when placing an order, so there is no need to explain them
                  every time.
                </li>
                <li>
                  <strong>Check before you go</strong> — the app lets users and carers check a venue's
                  physical accessibility details before making the trip: entrance type, ramp availability
                  and gradient, door type, disabled parking, surface material, and more.
                </li>
                <li>
                  <strong>Clear, calm interface</strong> — designed with cognitive accessibility in mind,
                  offering a simple layout and the ability to browse at your own pace without pressure.
                </li>
              </ul>

              <h3 id="a11y-carers">For Carers and Support Workers</h3>
              <ul aria-labelledby="a11y-carers">
                <li>Check a venue's accessibility details before leaving.</li>
                <li>Client requirements are shared with the venue automatically when ordering.</li>
                <li>Order ahead so carers can focus on the outing, not the logistics.</li>
                <li>Leave accessibility reviews to help other carers plan their visits.</li>
                <li>Direct the bill to someone else if needed.</li>
              </ul>
            </section>

            <section aria-labelledby="a11y-venues">
              <h2 id="a11y-venues">For Venues</h2>
              <p>
                Piatto helps venues understand and communicate their accessibility. When a venue joins
                Piatto, they can provide detailed information about their physical accessibility,
                including:
              </p>

              <h3 id="a11y-venue-data">Venue Accessibility Data</h3>
              <ul aria-labelledby="a11y-venue-data">
                <li>Front entrance type and door configuration</li>
                <li>Ramp availability, type (fixed or removable), and gradient compliance</li>
                <li>Elevator access and key requirements</li>
                <li>Certified disabled parking bays</li>
                <li>Surface material and incline from parking to venue entrance</li>
                <li>Footpath conditions and curb access</li>
              </ul>
              <p>
                This information is scored and displayed to customers, so they know what to expect
                before they arrive. Venues receive their guests' accessibility requirements before
                the order is placed, giving them time to prepare.
              </p>
            </section>

            <section aria-labelledby="a11y-ongoing">
              <h2 id="a11y-ongoing">Ongoing Efforts</h2>
              <p>Accessibility is a continuous process. We are actively working to:</p>
              <ul>
                <li>Expand screen reader testing across our web and mobile platforms.</li>
                <li>Improve colour contrast and visual clarity in all areas of the application.</li>
                <li>Add more detailed venue accessibility data points based on community feedback.</li>
                <li>Ensure new features are built with accessibility from the start.</li>
                <li>Engage with the disability community to understand where we can do better.</li>
              </ul>
            </section>

            <section aria-labelledby="a11y-feedback">
              <h2 id="a11y-feedback">Feedback</h2>
              <p>
                We welcome feedback on the accessibility of Piatto. If you encounter any barriers,
                have suggestions, or would like to report an accessibility issue, please contact us:
              </p>
              <address className="a11y-contact">
                <p><strong>Email:</strong> <a href="mailto:accessibility@piatto.com.au">accessibility@piatto.com.au</a></p>
              </address>
              <p>We aim to respond to accessibility feedback within two business days.</p>
            </section>
          </article>
        </main>

        <footer className="a11y-footer" role="contentinfo">
          <nav aria-label="Footer navigation">
            <Link to="/">Back to Home</Link>
          </nav>
        </footer>
      </div>

      <style jsx>{`
        .a11y-page {
          min-height: 100vh;
          background: #ffffff;
          color: #1a1a1a;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
          font-size: 18px;
          line-height: 1.8;
        }

        .a11y-skip {
          position: absolute;
          left: -9999px;
          top: 0;
          z-index: 200;
          background: #ffffff;
          color: #1a1a1a;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          text-decoration: none;
          border: 3px solid #c41e3a;
        }
        .a11y-skip:focus {
          left: 1rem;
          top: 1rem;
        }

        .a11y-header {
          background: rgba(10, 10, 10, 0.95);
          border-bottom: 1px solid rgba(255, 255, 255, 0.12);
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .a11y-header-inner {
          max-width: 900px;
          margin: 0 auto;
          padding: 0.75rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .a11y-logo-link { display: inline-flex; align-items: center; }
        .a11y-logo { height: 50px; width: auto; }
        .a11y-nav-link {
          color: #ffffff;
          text-decoration: none;
          font-size: 0.95rem;
          font-weight: 500;
        }
        .a11y-nav-link:hover,
        .a11y-nav-link:focus { color: rgba(255, 255, 255, 0.8); }

        .a11y-main {
          max-width: 900px;
          margin: 0 auto;
        }
        .a11y-content {
          padding: 3rem 1.5rem 4rem;
        }

        h1 {
          font-family: 'Lexend', system-ui, sans-serif;
          font-size: clamp(1.8rem, 4vw, 2.4rem);
          font-weight: 700;
          color: #111111;
          margin: 0 0 0.25rem;
          line-height: 1.3;
        }

        .a11y-updated {
          color: #555555;
          font-size: 0.9rem;
          margin-bottom: 2.5rem;
        }

        h2 {
          font-family: 'Lexend', system-ui, sans-serif;
          font-size: 1.5rem;
          font-weight: 600;
          color: #111111;
          margin: 2.5rem 0 0.75rem;
          padding-bottom: 0.4rem;
          border-bottom: 2px solid #7cb342;
        }

        h3 {
          font-family: 'Lexend', system-ui, sans-serif;
          font-size: 1.15rem;
          font-weight: 600;
          color: #222222;
          margin: 1.5rem 0 0.5rem;
        }

        p {
          margin: 0 0 1rem;
          color: #1a1a1a;
        }

        strong {
          color: #111111;
          font-weight: 700;
        }

        ul {
          margin: 0 0 1.25rem;
          padding-left: 1.5rem;
        }
        li {
          margin-bottom: 0.6rem;
          color: #1a1a1a;
        }

        a {
          color: #4a7c20;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        a:hover,
        a:focus {
          color: #3a6218;
        }
        a:focus-visible {
          outline: 3px solid #c41e3a;
          outline-offset: 3px;
        }

        address {
          font-style: normal;
          margin: 0.5rem 0 1rem;
        }
        address p {
          margin: 0.3rem 0;
        }

        .a11y-footer {
          max-width: 900px;
          margin: 0 auto;
          padding: 1.5rem 1.5rem 3rem;
          border-top: 1px solid #e0e0e0;
        }
        .a11y-footer a {
          color: #4a7c20;
          font-weight: 600;
          text-decoration: none;
        }
        .a11y-footer a:hover,
        .a11y-footer a:focus {
          text-decoration: underline;
        }

        @media (max-width: 600px) {
          .a11y-content { padding: 2rem 1rem 3rem; }
          h2 { font-size: 1.3rem; }
          h3 { font-size: 1.05rem; }
        }

        @media print {
          .a11y-header { position: static; background: #fff; border-bottom: 1px solid #ccc; }
          .a11y-nav-link { color: #111; }
          a { color: #111; }
        }
      `}</style>
    </>
  );
};

export default AccessibilityStatement;
